import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const createIconMapping = (benefitIcons) => ({
  "Sustainability_white.svg": benefitIcons.sustainability,
  "Excellence_white.svg": benefitIcons.excellence,
  "Reliability_white.svg": benefitIcons.reliability,
  "Customer_first_white.svg": benefitIcons.customerFirst,
  "Efficiently_white.svg": benefitIcons.efficiency,
});

function Benefits({ benefitIcons }) {
  const { t } = useTranslation();
  const benefitsData = t("components.aboutUs.benefits", {
    returnObjects: true,
  });
  const { heading, stats, values } = benefitsData;

  const IconMapping = useMemo(
    () => createIconMapping(benefitIcons),
    [benefitIcons]
  );

  const renderStat = (stat, index) => (
    <div key={index} className="text-center">
      <h3 className="text-2xl lg:text-3xl font-normal mb-2 relative inline-block text-gray-100">
        {stat.value}
        <span className="text-transparent bg-clip-text bg-[#ffa74e] text-sm md:text-base lg:text-lg absolute -top-1 -right-3">
          +
        </span>
      </h3>
      <p className="text-gray-400 text-sm md:text-base lg:text-lg">
        {stat.label}
      </p>
    </div>
  );

  const renderValue = (item, index) => (
    <div
      key={index}
      className="bg-gray-800 p-6 md:p-7 lg:p-8 rounded-3xl flex flex-col justify-between hover:shadow-lg hover:shadow-gray-700 transition-shadow"
      aria-label={item.title || item.brand}
    >
      {item.logo ? (
        <>
          <div className="flex items-center gap-2">
            <span className="text-lg lg:text-2xl text-transparent bg-clip-text bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450] flex items-center gap-2">
              {item.brand}
            </span>
          </div>
          <h3
            className="text-xl lg:text-3xl font-normal leading-tight text-gray-100"
            dangerouslySetInnerHTML={{ __html: item.heading }}
          />
        </>
      ) : (
        <>
          {item.icon && IconMapping[item.icon] ? (
            <img
              src={IconMapping[item.icon]}
              alt={item.subtitle || ""}
              className="w-10 h-10 lg:w-12 lg:h-12"
            />
          ) : (
            <div
              className="w-10 h-10 lg:w-12 lg:h-12 bg-gray-700 rounded-md"
              aria-hidden="true"
            />
          )}
          <div>
            <h3 className="text-lg lg:text-xl font-normal mb-2 lg:mb-3 text-gray-100">
              {item.title}
            </h3>
            <p className="text-md lg:text-lg text-transparent bg-clip-text bg-linear-to-r from-[#e1393e] via-[#fd6e4c] to-[#ffd450]">
              {item.subtitle}
            </p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <section className="w-full bg-black text-gray-200">
      <div className="container mx-auto py-8 md:py-12 lg:py-16 px-4 md:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-4 mb-12 lg:mb-24">
          <h2
            className="text-2xl lg:text-3xl font-normal text-center lg:text-left"
            dangerouslySetInnerHTML={{ __html: heading }}
          />
          <div className="grid grid-cols-2 lg:flex lg:items-center gap-6 lg:gap-12">
            {stats.map(renderStat)}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6 max-w-5xl mx-auto">
          {values.map(renderValue)}
        </div>
      </div>
    </section>
  );
}

Benefits.propTypes = {
  benefitIcons: PropTypes.shape({
    sustainability: PropTypes.string.isRequired,
    excellence: PropTypes.string.isRequired,
    reliability: PropTypes.string.isRequired,
    customerFirst: PropTypes.string.isRequired,
    efficiency: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(Benefits);
